import { create } from 'zustand';
import { VaccinationRecord, VaccinationFormData } from '@/types/vaccination';
import { vaccinationService } from '@/services/firebase/vaccinationService';

/**
 * ワクチン接種記録ストアの状態型定義
 */
interface VaccinationState {
  // 状態
  /** 現在表示中の個体の接種記録 */
  vaccinations: VaccinationRecord[];
  /** 期限切れ・期限間近の記録（ダッシュボード用） */
  upcomingVaccinations: VaccinationRecord[];
  loading: boolean;
  error: string | null;

  // アクション
  setVaccinations: (vaccinations: VaccinationRecord[]) => void;

  // API連携アクション
  fetchVaccinations: (dogId: string, userId: string) => Promise<void>;
  createVaccination: (
    dogId: string,
    data: VaccinationFormData,
    userId: string
  ) => Promise<void>;
  updateVaccination: (
    dogId: string,
    vaccinationId: string,
    data: VaccinationFormData,
    userId: string
  ) => Promise<void>;
  deleteVaccination: (
    dogId: string,
    vaccinationId: string,
    userId: string
  ) => Promise<void>;
  fetchUpcoming: (userId: string, withinDays: number) => Promise<void>;
}

/**
 * ワクチン接種記録管理ストア
 */
export const useVaccinationStore = create<VaccinationState>((set, get) => ({
  // 初期状態
  vaccinations: [],
  upcomingVaccinations: [],
  loading: false,
  error: null,

  setVaccinations: (vaccinations) => set({ vaccinations }),

  /**
   * 指定個体の接種記録を取得
   */
  fetchVaccinations: async (dogId: string, userId: string) => {
    set({ loading: true, error: null });
    try {
      const vaccinations = await vaccinationService.list(dogId, userId);
      set({ vaccinations, loading: false });
    } catch (error) {
      console.error('ワクチン接種記録取得エラー:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'ワクチン接種記録の取得に失敗しました',
        loading: false,
      });
    }
  },

  /**
   * 接種記録を作成し、一覧を再取得
   */
  createVaccination: async (dogId, data, userId) => {
    set({ loading: true, error: null });
    try {
      await vaccinationService.create(dogId, data, userId);
      const vaccinations = await vaccinationService.list(dogId, userId);
      set({ vaccinations, loading: false });
    } catch (error) {
      console.error('ワクチン接種記録作成エラー:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'ワクチン接種記録の作成に失敗しました',
        loading: false,
      });
      throw error;
    }
  },

  /**
   * 接種記録を更新し、一覧を再取得
   */
  updateVaccination: async (dogId, vaccinationId, data, userId) => {
    set({ loading: true, error: null });
    try {
      await vaccinationService.update(dogId, vaccinationId, data, userId);
      const vaccinations = await vaccinationService.list(dogId, userId);
      set({ vaccinations, loading: false });
    } catch (error) {
      console.error('ワクチン接種記録更新エラー:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'ワクチン接種記録の更新に失敗しました',
        loading: false,
      });
      throw error;
    }
  },

  /**
   * 接種記録を削除し、一覧から除外
   */
  deleteVaccination: async (dogId, vaccinationId, userId) => {
    set({ loading: true, error: null });
    try {
      await vaccinationService.delete(dogId, vaccinationId, userId);
      const { vaccinations } = get();
      set({
        vaccinations: vaccinations.filter((v) => v.id !== vaccinationId),
        loading: false,
      });
    } catch (error) {
      console.error('ワクチン接種記録削除エラー:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : 'ワクチン接種記録の削除に失敗しました',
        loading: false,
      });
      throw error;
    }
  },

  /**
   * 期限切れ・期限間近の記録を取得（ダッシュボード用）
   */
  fetchUpcoming: async (userId: string, withinDays: number) => {
    set({ loading: true, error: null });
    try {
      const upcomingVaccinations = await vaccinationService.listOverdueOrUpcoming(
        userId,
        withinDays
      );
      set({ upcomingVaccinations, loading: false });
    } catch (error) {
      console.error('期限間近ワクチン取得エラー:', error);
      set({
        error:
          error instanceof Error
            ? error.message
            : '期限間近のワクチン記録の取得に失敗しました',
        loading: false,
      });
    }
  },
}));
